import type { UgcEntry } from '../ugc/UgcApi'

/** Canonical UGC contentType for recorded gameplay clips — the de-facto "clips collection". */
export const CLIP_CONTENT_TYPE = 'clip'
// The upload size cap is read at runtime from files.getQuota().maxFileBytes,
// never hard-coded here, so the SDK never drifts from the backend limit.

export interface ClipAudioOptions {
  /** Capture the player's microphone. Default true. Gated by capture consent (T2 and below). */
  microphone?: boolean
  /** Capture game audio (requires useGameAudio to have registered a node). Default true. */
  gameAudio?: boolean
}

export type ClipPipPosition =
  | 'top-left' | 'top-center' | 'top-right'
  | 'bottom-left' | 'bottom-center' | 'bottom-right'

export interface ClipPipLayout {
  position?: ClipPipPosition
  widthFraction?: number
}

export interface ClipCameraOptions {
  facingMode?: 'user' | 'environment'
  pip?: ClipPipLayout
}

export interface StartClipRecordingOptions {
  /** The game canvas to record. Element, CSS selector, or omitted to auto-detect the first <canvas>. */
  canvas?: HTMLCanvasElement | string
  fps?: number
  /** Auto-stop after this many ms (default 60000). */
  maxDurationMs?: number
  /** Audio knobs; both default on. Omit for game audio + mic. */
  audio?: ClipAudioOptions
  /**
   * Enable webcam PiP overlay. `true` → front camera, bottom-right 25%.
   * Pass `ClipCameraOptions` for custom facing mode / layout.
   * Default `false` (no camera).
   */
  camera?: boolean | ClipCameraOptions
  /** Called when camera was requested but getUserMedia rejects (recording continues without PiP). */
  onCameraUnavailable?: () => void
  /**
   * 'auto' (default): the SDK samples the canvas continuously.
   * 'manual': the game drives frames by calling clips.captureFrame() in its render
   * loop (after world-render, before UI-render) so in-canvas UI is excluded.
   */
  captureMode?: 'auto' | 'manual'
  /** Force a specific MediaRecorder mime type (throws if unsupported). */
  mimeType?: string
  videoBitsPerSecond?: number
  /** Called when maxDurationMs caps capture; the caller should then call stopRecordingAsync(). */
  onAutoStop?: () => void
}

export interface ClipBlob {
  blob: Blob
  mimeType: string
  durationMs: number
  width: number
  height: number
  sizeBytes: number
}

export interface ClipPersistOptions {
  /** 'ugc' (default) uploads + registers a private UGC entry; 'none' returns only the blob. */
  persist?: 'none' | 'ugc'
  title?: string
  tags?: string[]
  /** Merge extra fields into the UGC data blob. */
  data?: Record<string, unknown>
}
// Clips persist PRIVATE; sharing is done later via publishClipAsync(id).

export interface ClipResult extends ClipBlob {
  /** Present when persisted as UGC. */
  ugc?: UgcEntry
  fileKey?: string
  /** Signed, expiring URL for immediate playback only — not durable, not persisted. */
  fileUrl?: string
}

export interface ClipsSupport {
  canRecord: boolean
  canUseMicrophone: boolean
  canUseCamera: boolean
  reason?: string
}

export type CaptureConsentStatus = 'granted' | 'denied' | 'undetermined'

export interface CaptureConsent {
  status: CaptureConsentStatus
  canAskAgain: boolean
}

export interface ClipsApi {
  isSupportedAsync(): Promise<ClipsSupport>
  /** Inspect the per-app capture consent without prompting. T1 apps always report granted. */
  getCaptureConsentAsync(): Promise<CaptureConsent>
  /**
   * Pop the app-owned native consent harness (T2 and below) to collect/refresh consent.
   * Pass `includesCamera` when the upcoming recording uses the webcam so the correct
   * device permission (camera vs microphone) drives the single-opt-in decision.
   */
  requestCaptureConsentAsync(opts?: { includesCamera?: boolean }): Promise<CaptureConsent>
  /** Register the game's master Web Audio output node so clips can capture game audio. */
  useGameAudio(node: AudioNode): void
  startRecordingAsync(options?: StartClipRecordingOptions): Promise<void>
  /**
   * In captureMode 'manual', snapshot the current game canvas into the recording
   * surface (call once per render frame, after world-render and before UI-render,
   * to exclude in-canvas UI). No-op when not recording or in 'auto' mode.
   */
  captureFrame(): void
  stopRecordingAsync(persist?: ClipPersistOptions): Promise<ClipResult>
  cancelRecordingAsync(): Promise<void>
  /** Promote a private clip UGC entry to shared (file + UGC visibility -> public). */
  publishClipAsync(ugcId: string): Promise<UgcEntry>
}
